import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import MacroDonut from "@/components/MacroDonut";
import { Play, Droplets, MoonStar } from "lucide-react";

const greetings = (h) => h < 5 ? "Late night." : h < 12 ? "Morning." : h < 17 ? "Afternoon." : h < 21 ? "Evening." : "Late night.";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [program, setProgram] = useState(null);
  const [nutrition, setNutrition] = useState(null);
  const [weekCount, setWeekCount] = useState(0);

  useEffect(() => {
    api.get("/program").then((r) => setProgram(r.data)).catch(() => {});
    api.get("/nutrition").then((r) => setNutrition(r.data)).catch(() => {});
    api.get("/progress").then((r) => setWeekCount(r.data.week?.workouts || 0)).catch(() => {});
  }, []);

  if (!program) return <div className="p-6 text-t3">Loading…</div>;
  const day = program.split[program.cursor];
  const now = new Date();
  const dateStr = now.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });

  return (
    <div className="min-h-screen bg-app max-w-md mx-auto px-5 pt-10 pb-4">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="overline">{greetings(now.getHours())}</div>
          <h1 className="font-display text-3xl uppercase mt-1" data-testid="dash-name">{user?.name?.split(" ")[0] || "Lifter"}.</h1>
        </div>
        <div className="text-right">
          <div className="overline">Today</div>
          <div className="text-sm text-t2 mt-1">{dateStr}</div>
        </div>
      </div>

      <div className="mt-4 card p-4 flex items-center justify-between" data-testid="dash-week">
        <div>
          <div className="overline">This week</div>
          <div className="font-display text-2xl mt-1 tabular">{weekCount} / {user?.daysPerWeek}</div>
        </div>
        <div className="flex gap-1">
          {Array.from({ length: user?.daysPerWeek || 3 }).map((_, i) => (
            <span key={i} className={`w-6 h-1.5 rounded-full ${i < weekCount ? "bg-lime" : ""}`} style={{ background: i < weekCount ? "var(--lime)" : "#1A1A1A" }} />
          ))}
        </div>
      </div>

      {/* Today's plan */}
      <div className="mt-4 card p-5" data-testid="dash-plan">
        <div className="flex items-center justify-between">
          <div className="overline">Round {program.round} · session {program.cursor + 1} of {program.split.length}</div>
          <div className="overline">{day.estMinutes} min</div>
        </div>
        <div className="mt-2 font-display text-4xl uppercase">{day.focus}</div>
        <div className="mt-1 text-t2 text-sm">{day.exercises.length} exercises · rest {day.exercises[0]?.rest || 75}s between sets</div>
        {program.warmups > 0 && <div className="mt-2 text-amber text-xs">Warm-up week · {program.warmups} deloaded session(s) left</div>}
        <button onClick={() => nav("/workout")} className="btn btn-primary w-full mt-5 pulse-slow" data-testid="dash-start">
          <Play size={16} /> Start
        </button>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <Tile k="Weight" v={user?.weightKg ? `${user.weightKg} kg` : "—"} tid="tile-weight" onClick={() => nav("/profile")} />
        <Tile k="Body fat" v={user?.bodyFatPct != null ? `${user.bodyFatPct}%` : "—"} tid="tile-bf" onClick={() => nav("/profile")} />
        <Tile k="Progress" v="↗" tid="tile-progress" onClick={() => nav("/progress")} />
      </div>

      {/* Fuel */}
      <div className="mt-4 card p-5" data-testid="fuel-card">
        <div className="flex justify-between items-baseline">
          <div className="overline">Fuel · daily target</div>
          {!nutrition?.locked && <div className="text-t3 text-xs">Suggested only</div>}
        </div>
        {nutrition?.locked ? (
          <div className="mt-4">
            <p className="text-t2 text-sm">Complete these to unlock macros:</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {nutrition.needs.map((n) => <span key={n} className="chip">{prettify(n)}</span>)}
            </div>
            <button className="btn btn-outline mt-4" onClick={() => nav("/profile")} data-testid="fuel-unlock">Complete profile</button>
          </div>
        ) : nutrition?.macros ? (
          <>
            <div className="mt-4">
              <MacroDonut macros={nutrition.macros} />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="card p-3 flex items-center gap-3"><Droplets size={16} className="text-t2" /><div><div className="overline">Water</div><div className="font-display text-lg tabular">{nutrition.macros.water_l} L</div></div></div>
              <div className="card p-3 flex items-center gap-3"><MoonStar size={16} className="text-t2" /><div><div className="overline">Sleep</div><div className="font-display text-lg tabular">{nutrition.macros.sleep_range} h</div></div></div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Tile({ k, v, tid, onClick }) {
  return (
    <button onClick={onClick} className="card p-3 text-left transition-colors hover:border-hair" data-testid={tid}>
      <div className="overline">{k}</div>
      <div className="font-display text-2xl mt-1 tabular">{v}</div>
    </button>
  );
}
function prettify(s) {
  return { weightKg: "Weight", bodyFatPct: "Body fat", activity: "Activity", goal: "Goal" }[s] || s;
}
